import { useState, useEffect, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useAppData } from "../../../context/AppDataContext";
import { useAuthInfo } from "../../../context/AuthContext";
import { errorToast, successfulToast } from "../../../util/toastNotifications";
import Loading from "../../layout/Loading";
import Form from "../../layout/Form";
import FormFieldText from "../../input/FormFieldText";
import Button from "../../core/Button";

const getInitialForm = () => ({
  org_id: "",
  name: "",
  email: "",
  phone: "",
  address: "",
  address2: "",
  city: "",
  state: "",
  zip_code: "",
  country: "",
});

const sanitizePhone = (value) =>
  value.replace(/[^0-9+\-()\s]/g, "").slice(0, 20);

const CompanyForm = () => {
  const { companyId } = useParams();
  const isEdit = Boolean(companyId);
  const history = useHistory();
  const { api } = useAppData();
  const { userInfo } = useAuthInfo();

  const [formFields, setFormFields] = useState(getInitialForm());
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);

  const isSuperAdmin = userInfo?.role?.name === "super-admin";

  const orgOptions = useMemo(() => {
    if (!organizations.length) {
      if (userInfo?.org_id) {
        return [
          {
            org_id: userInfo.org_id,
            name: userInfo.organization?.name || "Current organization",
          },
        ];
      }
      return [];
    }
    if (isSuperAdmin) return organizations;
    return organizations.filter(
      (o) => String(o.org_id) === String(userInfo.org_id)
    );
  }, [organizations, userInfo, isSuperAdmin]);

  useEffect(() => {
    let cancelled = false;

    if (!isSuperAdmin) {
      if (userInfo?.org_id && !cancelled) {
        setOrganizations([
          {
            org_id: userInfo.org_id,
            name: userInfo.organization?.name || "Current organization",
          },
        ]);
      }
      return () => {
        cancelled = true;
      };
    }

    const loadOrgs = async () => {
      try {
        const list = await api.org.getOrgs({ per_page: 200, page: 1 });
        if (!cancelled) {
          setOrganizations(Array.isArray(list) ? list : []);
        }
      } catch (error) {
        console.error("Error loading organizations:", error);
        if (!cancelled) {
          setOrganizations([]);
        }
      }
    };
    loadOrgs();
    return () => {
      cancelled = true;
    };
  }, [api, isSuperAdmin, userInfo?.org_id, userInfo?.organization?.name]);

  useEffect(() => {
    if (!isEdit) return;

    const loadCompany = async () => {
      try {
        setLoading(true);
        const data = await api.company.getCompanyById(companyId);
        setFormFields({
          org_id: data.org_id || "",
          name: data.name || "",
          email: data.email || "",
          phone: data.phone || "",
          address: data.address || "",
          address2: data.address2 || "",
          city: data.city || "",
          state: data.state || "",
          zip_code: data.zip_code || "",
          country: data.country || "",
        });
      } catch (error) {
        console.error("Error loading company:", error);
        errorToast("Failed to load company. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadCompany();
  }, [api, companyId, isEdit]);

  useEffect(() => {
    if (isEdit || !userInfo?.org_id) return;
    setFormFields((prev) =>
      prev.org_id ? prev : { ...prev, org_id: userInfo.org_id }
    );
  }, [isEdit, userInfo?.org_id]);

  const handleFieldUpdate = (e) => {
    const { name, value } = e.target;
    setFormFields((prev) => ({
      ...prev,
      [name]: name === "phone" ? sanitizePhone(value) : value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formFields.name.trim()) {
      errors.name = "Company name is required";
    }
    if (!formFields.org_id) {
      errors.org_id = "Organization is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    const orgId = formFields.org_id || userInfo.org_id;

    const payload = {
      org_id: orgId,
      name: formFields.name.trim(),
      email: formFields.email || null,
      phone: formFields.phone || null,
      address: formFields.address || null,
      address2: formFields.address2 || null,
      city: formFields.city || null,
      state: formFields.state || null,
      zip_code: formFields.zip_code || null,
      country: formFields.country || null,
      is_active: true,
      created_by_id: userInfo.user_id,
    };

    try {
      if (isEdit) {
        await api.company.updateCompany(companyId, payload);
        successfulToast("Company updated successfully");
        history.push(`/company/${companyId}`);
      } else {
        const newCompany = await api.company.createCompany(payload);
        successfulToast("Company created successfully");
        history.push(`/company/${newCompany.company_id}`);
      }
    } catch (error) {
      if (error?.status === 409) {
        setFormErrors((prev) => ({
          ...prev,
          name: "Company already exists",
        }));
        return;
      }
      errorToast("Unable to save company. Please try again.");
      setFormErrors((prev) => ({
        ...prev,
        name: prev.name || "Unable to save company. Try again.",
      }));
      return;
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) return <Loading />;

  const orgSelectDisabled = !isSuperAdmin && orgOptions.length === 1;

  return (
    <div className="form-styling px-16 py-8 card">
      <h2>{isEdit ? "Edit Company" : "New Company"}</h2>

      <Form onSubmit={handleSubmit}>
        <FormFieldText
          name="name"
          label="Company Name *"
          value={formFields.name}
          onChange={handleFieldUpdate}
          errorMessage={formErrors.name}
        />

        <div className="input-container">
          <label htmlFor="org_id">Organization *</label>
          <select
            id="org_id"
            name="org_id"
            value={formFields.org_id}
            onChange={handleFieldUpdate}
            disabled={orgSelectDisabled}
          >
            {!formFields.org_id && (
              <option value="">Select organization</option>
            )}
            {orgOptions.map((org) => (
              <option key={org.org_id} value={org.org_id}>
                {org.name}
              </option>
            ))}
          </select>
          <div className="error-msg">{formErrors.org_id}</div>
        </div>

        <FormFieldText
          name="email"
          label="Company Email"
          value={formFields.email}
          onChange={handleFieldUpdate}
        />
        <FormFieldText name="phone" label="Phone" value={formFields.phone} onChange={handleFieldUpdate} />
        <FormFieldText name="address" label="Address" value={formFields.address} onChange={handleFieldUpdate} />
        <FormFieldText name="address2" label="Address 2" value={formFields.address2} onChange={handleFieldUpdate} />
        <FormFieldText name="city" label="City" value={formFields.city} onChange={handleFieldUpdate} />
        <FormFieldText name="state" label="State" value={formFields.state} onChange={handleFieldUpdate} />
        <FormFieldText name="zip_code" label="Zip Code" value={formFields.zip_code} onChange={handleFieldUpdate} />
        <FormFieldText name="country" label="Country" value={formFields.country} onChange={handleFieldUpdate} />

        <div className="button-row">
          <Button cancel onClick={() => history.goBack()}>Cancel</Button>
          <button type="submit" disabled={loading}>
            {isEdit ? "Update Company" : "Create Company"}
          </button>
        </div>
      </Form>
    </div>
  );
};

export default CompanyForm;
